import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import { BehaviorSubject, Subscription, throwError, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

// INTERFACES
import {
  PickListInterface,
  PickListItemInterface,
  PickListItemTableInterface,
  PicklistPacking,
} from '../../../picklist';
import { IPagination } from '../../interfaces/paginator.interface';

// SERVICES
import { EnvironmentUtilService } from '../util';

import { LoadingDialogUtilService } from './../util/loading-dialog-util.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class PicklistApiService {
  private readonly _currentPicklist = new BehaviorSubject<PickListInterface | null>(
    <PickListInterface | null>null,
  );
  private readonly _currentPicklistItems = new BehaviorSubject<
    PickListItemInterface[]
  >(<PickListItemInterface[]>[]);
  private readonly _pagination = new BehaviorSubject<IPagination>(<IPagination>{});
  private readonly _currentPicklistPacking = new BehaviorSubject<PicklistPacking[]>(
    <PicklistPacking[]>[],
  );
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _updatingStatus = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loadingScan = new BehaviorSubject<boolean>(<boolean>false);

  currentPicklist$ = this._currentPicklist.asObservable();
  currentPicklistItems$ = this._currentPicklistItems.asObservable();
  loading$ = this._loading.asObservable();
  pagination$ = this._pagination.asObservable();
  submittingForm$ = this._submittingForm.asObservable();
  updatingStatus$ = this._updatingStatus.asObservable();
  currentPicklistPacking$ = this._currentPicklistPacking.asObservable();
  loadingScan$ = this._loadingScan.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
    private loadingDialogUtilService :LoadingDialogUtilService,
  ) {}

  getById(data :{
    id :number;
    params ?:{ [param :string] :string | string[] };
  }) :Subscription {
    const { id, params = {} } = data;
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/pick-and-pack/picklists/${id}`, {
        params,
      })
      .subscribe({
        next: (response) => {
          this._currentPicklist.next(response);
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getItems(params :{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/pick-and-pack/picklist-items`, {
        params,
      })
      .subscribe({
        next: (response) => {
          this._currentPicklistItems.next(response.data);
          this._pagination.next(response.meta);
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  ship(data :{ id :number; items :PickListItemTableInterface }) :Observable<any> {
    const { id, items } = data;

    this._submittingForm.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/pick-and-pack/picklist-items/ship`,
        body: {
          picklistId: id,
          items,
        },
      })
      .pipe(
        map((response :any) => {
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(() => response.error);
        }),
      );
  }

  savePicklist(data :{
    id :number;
    items :PickListItemTableInterface;
  }) :Observable<any> {
    const { id, items } = data;

    return this.apiService.post({
      path: `${this.environment.urlV2}/pick-and-pack/picklist-items/save`,
      body: {
        picklistId: id,
        items,
      },
    });
  }

  updatePicklistStatus(data :{
    id :number;
    picklistStatus :string;
  }) :Observable<any> {
    const { id } = data;
    this._loading.next(true);

    return this.apiService
      .put(`${this.environment.urlV2}/pick-and-pack/picklist-status/${id}`, data)
      .pipe(
        tap({
          next: (response) => {
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }

  shipPackingItem(data :{
    salesOrderId :number;
    picklistId :number;
    trackingNumber ?:string;
    carrierCode ?:string;
  }) :Observable<any> {
    const { salesOrderId, picklistId, carrierCode, trackingNumber } = data;

    this._updatingStatus.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/pick-and-pack/packing-items/ship`,
        body: {
          salesOrderId,
          picklistId,
          carrierCode,
          trackingNumber,
        },
      })
      .pipe(
        map((response :any) => {
          this._updatingStatus.next(false);

          return response;
        }),
        catchError((response) => {
          this._updatingStatus.next(false);

          return throwError(() => response.error);
        }),
      );
  }

  bulkShipPackingItem(data :{
    salesOrderIds :Array<number>;
    picklistId :number;
  }) :Observable<any> {
    const { salesOrderIds, picklistId } = data;

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/pick-and-pack/packing-items/bulk-ship`,
        body: {
          salesOrderIds,
          picklistId,
        },
      })
      .pipe(
        map((response :any) => {
          return response;
        }),
        catchError((response) => {
          return throwError(() => response.error);
        }),
      );
  }

  holdPackingItem(data :{
    salesOrderId :number;
    holdUntilDate :string;
  }) :Observable<any> {
    this._updatingStatus.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/sales/sales-orders/holduntil`,
        body: {
          ...data,
        },
      })
      .pipe(
        map((response :any) => {
          this._updatingStatus.next(false);

          return response;
        }),
        catchError((response) => {
          this._updatingStatus.next(false);

          return throwError(() => response.error);
        }),
      );
  }

  getPicklistPackingItems(picklistId :number) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(
        `${this.environment.urlV2}/pick-and-pack/packing-items/${picklistId}`,
        {},
      )
      .subscribe({
        next: (response) => {
          this._currentPicklistPacking.next(response);
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  setCurrentPicklist(picklist :PickListInterface | null) :void {
    this._currentPicklist.next(picklist);
  }

  exportPackagingSlip(params :{
    salesOrderId :string;
    exportType :string;
  }) :Subscription {
    return this.apiService
      .getBlob(
        `${this.environment.urlV2}/pick-and-pack/picklists/packingSlip-export`,
        {
          params,
        },
      )
      .subscribe({
        next: (response) => {
          FileSaver.saveAs(
            response,
            'Packing Slip_#' + params.salesOrderId + '.pdf',
          );
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  verifyScanItems(data :{ picklistOrderId :number }) :Observable<any> {
    const { picklistOrderId } = data;

    this._submittingForm.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/pick-and-pack/picklist-items/${picklistOrderId}/verify`,
      })
      .pipe(
        map((response :any) => {
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(() => response.error);
        }),
      );
  }

  bulkVerifyScanItems(data :{ picklistOrderIds :Array<number> }) :Observable<any> {
    const { picklistOrderIds } = data;

    return this.apiService.post({
      path: `${this.environment.urlV2}/pick-and-pack/picklist-items/bulk-verify`,
      body: {
        picklistOrderIds,
      },
    });
  }

  scanBarcode(data :{
    orderId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { orderId, params } = data;

    this._loadingScan.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/sales/sales-orders/${orderId}/search-item`, {
        params,
      })
      .pipe(
        map((response :any) => {
          this._loadingScan.next(false);

          return response;
        }),
        catchError((response) => {
          this._loadingScan.next(false);

          return throwError(() => response.error);
        }),
      );
  }

  autoGeneratePickAndPack(data :{ salesOrders :number[] }) :Observable<any> {
    const { salesOrders } = data;

    this._loading.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/pick-and-pack/picklists/autogenerate-pick-pack`,
        body: {
          salesOrders: salesOrders,
          fulfill: false,
        },
      })
      .pipe(
        map((response :any) => {
          this._loading.next(false);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(() => response.error);
        }),
      );
  }

  flagSalesOrder(data :{
    salesOrderId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { salesOrderId, params } = data;

    return this.apiService
      .get(`${this.environment.urlV2}/orders/sales/flag/${salesOrderId}`, {
        params,
      })
      .pipe(
        map((response :any) => {
          return response;
        }),
        catchError((response) => {
          return throwError(() => response.error);
        }),
      );
  }

  unflagSalesOrder(data :{ salesOrderId :number }) :Observable<any> {
    const { salesOrderId } = data;

    return this.apiService.get(
      `${this.environment.urlV2}/orders/sales/${salesOrderId}/unflag`,
    );
  }

  getPickingItemsBySalesOrderIds(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/sales/shipstation_sales/picking_items`,
      {
        params,
      },
    );
  }

  bulkCreatePicklist(params :{
    salesOrders :number[];
    fulfill :boolean;
    createPerOrder ?:boolean;
  }) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/pick-and-pack/picklists/sales-order`,
      body: {
        ...params,
      },
    });
  }

  exportBulkPackingSlip(params ?:{
    [key :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.getBlob(
      `${this.environment.urlV2}/pick-and-pack/picklists/packingslip-bulk-export`,
      {
        params: params,
      },
    );
  }

  bulkExportPackagingSlip(params :{
    salesOrderIds :string;
    exportType :string;
  }) :Subscription {
    return this.apiService
      .getBlob(
        `${this.environment.urlV2}/pick-and-pack/picklists/packingslip-bulk-export`,
        {
          params,
        },
      )
      .subscribe({
        next: (response) => {
          FileSaver.saveAs(response, 'Packing Slips.pdf');
          this.loadingDialogUtilService.close();
        },
        error: (error) => {
          this.loadingDialogUtilService.close();
          this.loadingDialogUtilService.showDialogMessage('Error Export');
        },
      });
  }

  getPickingItemsBySalesOrderId(salesOrderId :number) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/sales/shipstation_sales/${salesOrderId}/picking_items`,
    );
  }

  exportLabelsWithPackingSlip(params :{
    salesOrderIds :string;
    packingSlipPageSize :string;
  }) :Observable<any> {
    return this.apiService.getBlob(
      `${this.environment.urlV2}/pick-and-pack/picklists/export-labels-with-packing-slip/download`,
      {
        params,
      },
    );
  }
}
